import React, { useMemo, useState } from "react";
import {
  ActivityList,
  EmptyState,
  LoadingScreen,
  Metric,
  PageTitle,
  PortalShell,
  StatusPill,
  WorkflowBar,
  capacityMt,
  collectDocuments,
  dateTime,
  districtStatus,
  downloadCsv,
  hasRegistrarPackage,
  hasSubsidyFile,
  human,
  isDistrictComplianceReceived,
  isForwardedToState,
  isPmcForwardedToDistrict,
  isStateApproved,
  locationText,
  normal,
  number,
  projectId,
  projectName,
  registrarPackageUrl,
  runRegistrarAction,
  stageLabel,
  stateStatus,
  subsidyPackage,
  uploadOfficialFile,
  upper,
  useRegistrarData,
  useRegistrarSession,
} from "./RegistrarPortalCore";

const INITIAL_FORM = {
  inwardNumber: "",
  physicalFileNumber: "",
  receivedDate: "",
  pageCount: "",
  suoMotoStatus: "CLEAR",
  dispatchNumber: "",
  dispatchDate: "",
  meetingNumber: "",
  meetingDate: "",
  meetingTime: "",
  venue: "",
  decision: "APPROVED",
  remarks: "",
};

const ACTION_TITLES = {
  inward: "Record District Physical-File Inward",
  verify: "Complete District Scrutiny",
  query: "Raise Official Query",
  stateQueryToPmc: "Forward State Query to PMC",
  stateComplianceForward: "Forward PMC Compliance to State Registrar",
  forward: "Forward Verified File to State Registrar",
  dcdcSchedule: "Schedule DCDC Meeting",
  dcdcDecision: "Record DCDC Decision",
  subsidyQuery: "Raise Subsidy File Query",
  subsidyForward: "Forward Subsidy File to State",
};

// Local compatibility widgets: no new export is required from the project's
// existing RegistrarPortalCore.js.
const LOCAL_WIDGET_CSS = `
  .rg-document-control,.rg-automation{border-top:4px solid #19764a}.rg-document-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.rg-document-grid article{display:grid;grid-template-columns:34px minmax(0,1fr) auto;align-items:center;gap:10px;padding:12px;border:1px solid #d5e0eb;border-radius:9px;background:#fff}.rg-document-grid article.ready{border-left:4px solid #19764a}.rg-document-grid article.pending{border-left:4px solid #c7d2dc;background:#f8fafc}.rg-document-grid article>i{width:29px;height:29px;border-radius:8px;display:grid;place-items:center;background:#e7f5ec;color:#19764a;font-style:normal;font-size:10px;font-weight:900}.rg-document-grid article.pending>i{background:#e9eef3;color:#73869a}.rg-document-grid article div{display:flex;min-width:0;flex-direction:column}.rg-document-grid article b{font-size:11px;color:#092f5b}.rg-document-grid article span{margin-top:4px;font-size:9px;color:#60748a}.rg-document-grid article a{border:1px solid #aabed0;border-radius:6px;padding:7px 8px;color:#1769b0;text-decoration:none;font-size:9px;font-weight:900;white-space:nowrap}.rg-document-grid article em{font-size:9px;color:#8b99a8;white-space:nowrap}.rg-automation-list{display:flex;flex-direction:column}.rg-automation-list article{display:grid;grid-template-columns:76px minmax(190px,1.15fr) minmax(220px,1.4fr) 86px auto;align-items:center;gap:13px;padding:12px 2px;border-bottom:1px solid #e2e9f0}.rg-automation-list article:last-child{border-bottom:0}.rg-automation-list article>div{display:flex;flex-direction:column}.rg-automation-list article b{font-size:11px;color:#092f5b}.rg-automation-list article small,.rg-automation-list article time{font-size:9px;color:#60748a}.rg-automation-list article p{font-size:11px;color:#425c73;margin:0}.rg-priority{display:inline-flex;justify-content:center;padding:6px 8px;border-radius:999px;background:#e7f0fb;color:#1c5a98;font-size:8px;font-weight:950}.rg-priority.high{background:#fff0d2;color:#945d08}.rg-priority.critical{background:#fde7e5;color:#a02f29}@media(max-width:720px){.rg-document-grid{grid-template-columns:1fr}.rg-document-grid article{grid-template-columns:30px minmax(0,1fr)}.rg-document-grid article a,.rg-document-grid article em{grid-column:2}.rg-automation-list article{grid-template-columns:70px minmax(0,1fr)}.rg-automation-list article p,.rg-automation-list article time,.rg-automation-list article button{grid-column:2}}
`;

const authorityDocuments = (project = {}) => {
  const source = collectDocuments(project);
  const definitions = [
    ["PACS Registrar Package", "PACS", /registrar.*(package|zip)|package.*registrar/i, true],
    ["PMC Final Signed DPR", "PMC", /dpr|detailed project report/i, true],
    ["Fully Executed PACS–PMC MOU", "PACS & PMC", /mou|memorandum/i, true],
    ["NAFED Assurance Letter", "NAFED / PMC", /assurance|hiring assurance/i, true],
    ["District Scrutiny & Forwarding Letter", "District Registrar", /district.*(forward|scrutiny)|forwarding/i, false],
    ["State Permission / Scrutiny Order", "State Registrar", /state.*(permission|scrutiny)|permission order/i, false],
    ["Signed DCDC Decision Letter", "District DCDC", /dcdc.*(decision|letter)/i, false],
  ];
  return definitions.map(([label, owner, pattern, required], index) => ({
    key: `${index}-${label}`,
    label,
    owner,
    required,
    document: source.find((item) =>
      pattern.test(`${item.name || ""} ${item.category || ""} ${item.path || ""}`),
    ),
  }));
};

const documentReadiness = (project = {}) => {
  const required = authorityDocuments(project).filter((item) => item.required);
  return {
    complete: required.filter((item) => item.document).length,
    total: required.length,
  };
};

function DocumentControl({ project }) {
  const documents = authorityDocuments(project);
  const readiness = documentReadiness(project);
  return (
    <section className="rg-panel rg-document-control">
      <style>{LOCAL_WIDGET_CSS}</style>
      <div className="rg-panel-head"><div><small>UNIFIED AUTHORITY DOCUMENT CONTROL</small><h3>Official Project Document Matrix</h3><p>PACS, PMC, District and State files from the same live project record.</p></div><StatusPill value={`${readiness.complete}/${readiness.total} mandatory ready`} /></div>
      <div className="rg-document-grid">{documents.map((item, index) => (
        <article className={item.document ? "ready" : "pending"} key={item.key}><i>{item.document ? "✓" : String(index + 1).padStart(2, "0")}</i><div><b>{item.label}</b><span>{item.owner} · {item.required ? "Mandatory" : "Workflow document"}</span></div>{item.document ? <a href={item.document.url} target="_blank" rel="noreferrer">View / Download</a> : <em>Not available yet</em>}</article>
      ))}</div>
    </section>
  );
}

const actionAge = (value) => {
  const date = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : new Date(value || 0);
  const time = Number.isNaN(date.getTime()) ? Date.now() : date.getTime();
  return Math.max(0, Math.floor((Date.now() - time) / 86400000));
};

const districtNextAction = (project) => {
  if (stateStatus(project).includes("RETURNED_TO_DISTRICT") && !/STATE_QUERY_FORWARDED_TO_PMC|PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED|COMPLIANCE_FORWARDED_TO_STATE/.test(districtStatus(project))) return ["State Query", "Forward the State Registrar query to the allocated PMC", "queries"];
  if (districtStatus(project).includes("PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED")) return ["State Compliance", "Verify PMC response and forward it to State Registrar", "queries"];
  if (/QUERY|REJECT|ADVERSE/.test(districtStatus(project))) return ["Compliance", "Track allocated PMC compliance", "queries"];
  if (isPmcForwardedToDistrict(project) && project.districtRegistrarVerified !== true && !districtStatus(project).includes("VERIFIED")) return ["Scrutiny", "Check complete ZIP and all linked documents", "scrutiny"];
  if ((project.districtRegistrarVerified === true || districtStatus(project).includes("VERIFIED")) && !isForwardedToState(project)) return ["Dispatch", "Upload letter and forward to State Registrar", "forward"];
  if (isStateApproved(project) && !project.dcdcMeetingDate) return ["DCDC", "Schedule DCDC meeting", "dcdc"];
  if (isStateApproved(project) && project.dcdcMeetingDate && !["APPROVED", "REJECTED"].includes(String(project.dcdcStatus || "").toUpperCase())) return ["DCDC", "Record DCDC decision and signed letter", "dcdc"];
  return null;
};

function AutomationQueue({ projects = [], onOpen, limit = 8 }) {
  const rows = projects.map((project) => {
    const action = districtNextAction(project);
    if (!action) return null;
    const ageDays = actionAge(project.updatedAt || project.createdAt);
    return { project, queue: action[0], nextAction: action[1], actionKey: action[2], ageDays, priority: ageDays >= 7 ? "CRITICAL" : ageDays >= 3 ? "HIGH" : "DUE" };
  }).filter(Boolean).sort((a, b) => b.ageDays - a.ageDays).slice(0, limit);
  return (
    <section className="rg-panel rg-automation">
      <style>{LOCAL_WIDGET_CSS}</style>
      <div className="rg-panel-head"><div><small>AUTOMATED NEXT-ACTION ENGINE</small><h3>Priority Authority Queue</h3><p>Pending action and ageing are calculated from live project status.</p></div><StatusPill value={`${rows.length} priority files`} /></div>
      {rows.length ? <div className="rg-automation-list">{rows.map((item) => (
        <article key={item.project.id}><span className={`rg-priority ${normal(item.priority)}`}>{human(item.priority)}</span><div><b>{projectName(item.project)}</b><small>{projectId(item.project)} · {item.queue}</small></div><p>{item.nextAction}</p><time>{item.ageDays} day ageing</time><button className="rg-link" onClick={() => onOpen(item.project, item.actionKey)}>Open File →</button></article>
      ))}</div> : <EmptyState title="All authority queues are current" text="No actionable file is waiting in this jurisdiction." />}
    </section>
  );
}

function ProjectRegister({
  title,
  description,
  rows,
  search,
  setSearch,
  onOpen,
  emptyText,
}) {
  const filtered = rows.filter((project) =>
    normal(
      `${projectId(project)} ${projectName(project)} ${locationText(project)} ${project.assignedPmcName || project.pmcName}`,
    ).includes(normal(search)),
  );
  return (
    <section className="rg-panel">
      <div className="rg-panel-head">
        <div>
          <small>LIVE DISTRICT REGISTER</small>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <aside>
          <StatusPill value={`${filtered.length} files`} />
        </aside>
      </div>
      <div className="rg-filters" style={{ gridTemplateColumns: "1fr auto" }}>
        <input
          className="rg-input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search PACS, application number, village, taluka or PMC..."
        />
        <button className="rg-btn secondary" onClick={() => setSearch("")}>
          Clear
        </button>
      </div>
      {filtered.length ? (
        <div className="rg-table-wrap">
          <table className="rg-table">
            <thead>
              <tr>
                <th>Application / PACS</th>
                <th>Location</th>
                <th>Capacity</th>
                <th>Allocated PMC</th>
                <th>Mandatory Documents</th>
                <th>Current Stage</th>
                <th>Updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((project) => (
                <tr key={project.id}>
                  <td>
                    <b>{projectName(project)}</b>
                    <small>{projectId(project)}</small>
                  </td>
                  <td>{locationText(project)}</td>
                  <td>{number(capacityMt(project))} MT</td>
                  <td>{project.assignedPmcName || project.pmcName || "-"}</td>
                  <td>
                    <StatusPill
                      value={`${documentReadiness(project).complete}/${documentReadiness(project).total} ready`}
                    />
                  </td>
                  <td>
                    <StatusPill value={stageLabel(project)} />
                  </td>
                  <td>{dateTime(project.updatedAt || project.createdAt)}</td>
                  <td>
                    <button className="rg-link" onClick={() => onOpen(project)}>
                      Open Official File →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No file in this stage" text={emptyText} />
      )}
    </section>
  );
}

function CaseDrawer({ project, activeModule, onClose, onAction }) {
  if (!project) return null;
  const documents = collectDocuments(project);
  const packageUrl = registrarPackageUrl(project);
  const subsidyUrl = subsidyPackage(project)?.url;
  return (
    <div className="rg-drawer-backdrop" onMouseDown={onClose}>
      <aside
        className="rg-drawer"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="rg-drawer-head">
          <div>
            <small>{projectId(project)}</small>
            <h2>{projectName(project)}</h2>
            <p>{locationText(project)}</p>
          </div>
          <button onClick={onClose}>×</button>
        </header>
        <div className="rg-drawer-body">
          <section className="rg-panel">
            <WorkflowBar project={project} />
            <div className="rg-summary">
              <div>
                <span>Capacity</span>
                <b>{number(capacityMt(project))} MT</b>
              </div>
              <div>
                <span>PMC</span>
                <b>{project.assignedPmcName || project.pmcName || "-"}</b>
              </div>
              <div>
                <span>District Status</span>
                <b>{human(districtStatus(project))}</b>
              </div>
              <div>
                <span>Current Stage</span>
                <b>{stageLabel(project)}</b>
              </div>
            </div>
          </section>

          <DocumentControl project={project} />

          {(project.districtRegistrarRemarks || project.stateRegistrarRemarks || project.pmcRegistrarComplianceResponse) && (
            <section className="rg-panel" style={{ borderLeft: "5px solid #d08a18" }}>
              <div className="rg-panel-head"><div><small>LIVE QUERY & COMPLIANCE</small><h3>Official Clarification Trail</h3><p>Every query and response stays attached to this PACS application.</p></div></div>
              <div className="rg-summary">
                <div><span>District Query</span><b>{project.districtRegistrarRemarks || "-"}</b></div>
                <div><span>PMC Response</span><b>{project.pmcRegistrarComplianceResponse || "-"}</b></div>
                <div><span>State Query</span><b>{project.stateRegistrarRemarks || "-"}</b></div>
                <div><span>State-query PMC Response</span><b>{project.pmcStateQueryComplianceResponse || "-"}</b></div>
              </div>
            </section>
          )}

          <section className="rg-panel">
            <div className="rg-panel-head">
              <div>
                <small>SERIAL DOCUMENT FILE</small>
                <h3>Registrar Package & Linked Documents</h3>
                <p>All documents are live links from this PACS project.</p>
              </div>
              <aside>
                {packageUrl && (
                  <a
                    className="rg-btn secondary"
                    href={packageUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download Registrar ZIP
                  </a>
                )}
                {subsidyUrl && (
                  <a
                    className="rg-btn secondary"
                    href={subsidyUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download Subsidy ZIP
                  </a>
                )}
              </aside>
            </div>
            {documents.length ? (
              <div className="rg-doc-list">
                {documents.map((document) => (
                  <a
                    href={document.url}
                    key={document.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <b>{document.name}</b>
                    <span>{document.category} · View / Download</span>
                  </a>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No linked documents"
                text="PACS/PMC must complete the Registrar package first."
              />
            )}
          </section>

          <section className="rg-panel">
            <div className="rg-panel-head">
              <div>
                <small>AUTHORITY ACTION</small>
                <h3>District Registrar Decision Desk</h3>
                <p>
                  Only actions valid for the current file stage are enabled.
                </p>
              </div>
            </div>
            <div
              className="rg-actions"
              style={{ justifyContent: "flex-start" }}
            >
              {project.enableLegacyPhysicalInward === true &&
                !project.districtPhysicalFileReceived &&
                isPmcForwardedToDistrict(project) && (
                  <button
                    className="rg-btn"
                    onClick={() => onAction("inward", project)}
                  >
                    Record Optional Physical Inward
                  </button>
                )}
              {isPmcForwardedToDistrict(project) &&
                !isForwardedToState(project) &&
                !stateStatus(project).includes("RETURNED_TO_DISTRICT") && (
                  <>
                    <button
                      className="rg-btn green"
                      onClick={() => onAction("verify", project)}
                    >
                      Complete Scrutiny
                    </button>
                    <button
                      className="rg-btn orange"
                      onClick={() => onAction("query", project)}
                    >
                      Raise Query
                    </button>
                  </>
                )}
              {stateStatus(project).includes("RETURNED_TO_DISTRICT") &&
                !/STATE_QUERY_FORWARDED_TO_PMC|PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED|COMPLIANCE_FORWARDED_TO_STATE/.test(districtStatus(project)) && (
                  <button className="rg-btn orange" onClick={() => onAction("stateQueryToPmc", project)}>Forward State Query to PMC</button>
                )}
              {districtStatus(project).includes("PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED") && (
                <button className="rg-btn green" onClick={() => onAction("stateComplianceForward", project)}>Forward Compliance to State</button>
              )}
              {(project.districtRegistrarVerified === true ||
                districtStatus(project).includes("VERIFIED")) &&
                !isForwardedToState(project) && (
                  <button
                    className="rg-btn"
                    onClick={() => onAction("forward", project)}
                  >
                    Forward to State Registrar
                  </button>
                )}
              {isStateApproved(project) && !project.dcdcMeetingDate && (
                <button
                  className="rg-btn"
                  onClick={() => onAction("dcdcSchedule", project)}
                >
                  Schedule DCDC
                </button>
              )}
              {isStateApproved(project) && project.dcdcMeetingDate && (
                <button
                  className="rg-btn green"
                  onClick={() => onAction("dcdcDecision", project)}
                >
                  Record DCDC Decision
                </button>
              )}
              {hasSubsidyFile(project) && (
                <>
                  <button
                    className="rg-btn orange"
                    onClick={() => onAction("subsidyQuery", project)}
                  >
                    Subsidy Query
                  </button>
                  <button
                    className="rg-btn green"
                    onClick={() => onAction("subsidyForward", project)}
                  >
                    Forward Subsidy to State
                  </button>
                </>
              )}
            </div>
          </section>

          <section className="rg-panel">
            <div className="rg-panel-head">
              <div>
                <small>FILE CONTROL DATA</small>
                <h3>Physical & DCDC Record</h3>
              </div>
            </div>
            <div className="rg-summary">
              <div>
                <span>District Inward</span>
                <b>{project.districtInwardNumber || "-"}</b>
              </div>
              <div>
                <span>Physical File No.</span>
                <b>{project.districtPhysicalFileNumber || "-"}</b>
              </div>
              <div>
                <span>Suo-moto</span>
                <b>{human(project.suoMotoStatus || "Pending")}</b>
              </div>
              <div>
                <span>State Dispatch</span>
                <b>{project.districtToStateDispatchNumber || "-"}</b>
              </div>
              <div>
                <span>DCDC Meeting</span>
                <b>{project.dcdcMeetingDate || "-"}</b>
              </div>
              <div>
                <span>DCDC Status</span>
                <b>{human(project.dcdcStatus || "Pending")}</b>
              </div>
              <div>
                <span>Subsidy Status</span>
                <b>{human(project.districtSubsidyStatus || "Pending")}</b>
              </div>
              <div>
                <span>Module</span>
                <b>{human(activeModule)}</b>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}

function ActionModal({
  type,
  form,
  setForm,
  setFile,
  onClose,
  onSubmit,
  busy,
}) {
  if (!type) return null;
  const field = (key, label, inputType = "text", required = false) => (
    <div className="rg-field">
      <label>
        {label}
        {required ? " *" : ""}
      </label>
      <input
        className="rg-input"
        type={inputType}
        value={form[key]}
        required={required}
        onChange={(event) =>
          setForm((current) => ({ ...current, [key]: event.target.value }))
        }
      />
    </div>
  );
  return (
    <div className="rg-modal-backdrop" onMouseDown={onClose}>
      <div
        className="rg-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <h2>{ACTION_TITLES[type]}</h2>
          <button onClick={onClose}>×</button>
        </header>
        <section>
          <div className="rg-form-grid">
            {type === "inward" && (
              <>
                {field("inwardNumber", "District inward number", "text", true)}
                {field(
                  "physicalFileNumber",
                  "Physical file number",
                  "text",
                  true,
                )}
                {field("receivedDate", "Physical receipt date", "date", true)}
                {field("pageCount", "Total physical pages", "number", true)}
              </>
            )}
            {type === "verify" && (
              <div className="rg-field wide">
                <label>Suo-moto / adverse status *</label>
                <select
                  className="rg-select"
                  value={form.suoMotoStatus}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      suoMotoStatus: event.target.value,
                    }))
                  }
                >
                  <option value="CLEAR">Clear — no adverse proceeding</option>
                  <option value="ADVERSE">
                    Adverse / suo-moto proceeding exists
                  </option>
                </select>
              </div>
            )}
            {type === "forward" && (
              <>
                {field(
                  "dispatchNumber",
                  "District dispatch number",
                  "text",
                  true,
                )}
                {field("dispatchDate", "Dispatch date", "date", true)}
                <div className="rg-field wide">
                  <label>Signed District scrutiny / forwarding letter PDF (optional)</label>
                  <input
                    className="rg-input"
                    type="file"
                    accept="application/pdf"
                    onChange={(event) =>
                      setFile(event.target.files?.[0] || null)
                    }
                  />
                </div>
              </>
            )}
            {type === "dcdcSchedule" && (
              <>
                {field("meetingNumber", "DCDC meeting number", "text", true)}
                {field("meetingDate", "Meeting date", "date", true)}
                {field("meetingTime", "Meeting time", "time", true)}
                {field("venue", "Meeting venue", "text", true)}
              </>
            )}
            {type === "dcdcDecision" && (
              <>
                <div className="rg-field">
                  <label>DCDC decision *</label>
                  <select
                    className="rg-select"
                    value={form.decision}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        decision: event.target.value,
                      }))
                    }
                  >
                    <option value="APPROVED">Approved</option>
                    <option value="QUERY">Query / compliance</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
                <div className="rg-field">
                  <label>
                    Signed DCDC letter PDF{" "}
                    {form.decision !== "QUERY" ? "*" : ""}
                  </label>
                  <input
                    className="rg-input"
                    type="file"
                    accept="application/pdf"
                    onChange={(event) =>
                      setFile(event.target.files?.[0] || null)
                    }
                  />
                </div>
              </>
            )}
            {type === "subsidyForward" &&
              field("dispatchNumber", "Subsidy dispatch number", "text", true)}
            <div className="rg-field wide">
              <label>Official remarks *</label>
              <textarea
                className="rg-textarea"
                value={form.remarks}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    remarks: event.target.value,
                  }))
                }
                placeholder="Write clear authority remarks for the permanent audit trail..."
              />
            </div>
          </div>
          <div className="rg-actions">
            <button className="rg-btn secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="rg-btn green" disabled={busy} onClick={onSubmit}>
              {busy ? "Saving..." : "Save Official Action"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default function NafedDistrictRegistrarDashboard() {
  const {
    profile,
    loading: sessionLoading,
    error: sessionError,
  } = useRegistrarSession(["DISTRICT_REGISTRAR", "DISTRICT_REG"]);
  const { projects, news, loading, error } = useRegistrarData(
    profile,
    "DISTRICT",
  );
  const [active, setActive] = useState("overview");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [actionType, setActionType] = useState("");
  const [actionProjectId, setActionProjectId] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const selectedProject =
    projects.find((project) => project.id === selectedId) || null;
  const actionProject =
    projects.find((project) => project.id === actionProjectId) || null;

  const stages = useMemo(() => {
    const inbox = projects.filter(
      (project) =>
        isPmcForwardedToDistrict(project) &&
        !isForwardedToState(project),
    );
    const isExceptionFile = (project) =>
      /QUERY|REJECT|ADVERSE|SUO_MOTO/.test(districtStatus(project));
    const scrutiny = projects.filter(
      (project) =>
        isPmcForwardedToDistrict(project) &&
        !isForwardedToState(project) &&
        !isExceptionFile(project) &&
        project.districtRegistrarVerified !== true &&
        !districtStatus(project).includes("VERIFIED"),
    );
    const queries = projects.filter(
      (project) =>
        isExceptionFile(project) ||
        stateStatus(project).includes("RETURNED_TO_DISTRICT") ||
        districtStatus(project).includes("STATE_QUERY"),
    );
    const forward = projects.filter(
      (project) =>
        !isForwardedToState(project) &&
        (project.districtRegistrarVerified === true ||
          districtStatus(project).includes("VERIFIED")) &&
        String(project.suoMotoStatus || "").toUpperCase() === "CLEAR",
    );
    const dcdc = projects.filter(isStateApproved);
    const subsidy = projects.filter(hasSubsidyFile);
    return { all: projects, inbox, scrutiny, queries, forward, dcdc, subsidy };
  }, [projects]);

  const metrics = useMemo(
    () => ({
      capacity: projects.reduce(
        (total, project) => total + capacityMt(project),
        0,
      ),
      forwarded: projects.filter(isForwardedToState).length,
      dcdcApproved: projects.filter(
        (project) => String(project.dcdcStatus).toUpperCase() === "APPROVED",
      ).length,
      pacs: new Set(
        projects.map(
          (project) =>
            project.pacsUserId ||
            project.submittedByUid ||
            projectName(project),
        ),
      ).size,
      pmc: new Set(
        projects
          .map((project) => project.assignedPmcId || project.assignedPmcName)
          .filter(Boolean),
      ).size,
    }),
    [projects],
  );

  const navigation = [
    { key: "overview", label: "District Overview" },
    { key: "all", label: "All PACS Applications", count: projects.length },
    { key: "inbox", label: "PMC Forwarded Files", count: stages.inbox.length },
    {
      key: "scrutiny",
      label: "Document Scrutiny",
      count: stages.scrutiny.length,
    },
    {
      key: "queries",
      label: "Query & Compliance",
      count: stages.queries.length,
    },
    { key: "forward", label: "Forward to State", count: stages.forward.length },
    { key: "dcdc", label: "DCDC Decision Desk", count: stages.dcdc.length },
    { key: "activity", label: "Live Activity", count: news.length },
    { key: "reports", label: "District MIS Reports" },
  ];

  const openAction = (type, project) => {
    setActionType(type);
    setActionProjectId(project.id);
    setForm({
      ...INITIAL_FORM,
      receivedDate: new Date().toISOString().slice(0, 10),
      dispatchDate: new Date().toISOString().slice(0, 10),
      remarks: "",
    });
    setFile(null);
    setMessage("");
  };
  const requireValue = (value, label) => {
    if (!String(value || "").trim()) throw new Error(`${label} is required.`);
  };

  const saveAction = async () => {
    if (!actionProject || !profile || !actionType) return;
    setBusy(true);
    setMessage("");
    try {
      let action = "";
      let notificationType = "";
      let description = "";
      let patch = {};
      let targetRoles = ["PACS", "PMC"];
      const nowIso = new Date().toISOString();
      if (actionType === "inward") {
        requireValue(form.inwardNumber, "District inward number");
        requireValue(form.physicalFileNumber, "Physical file number");
        requireValue(form.receivedDate, "Receipt date");
        requireValue(form.pageCount, "Page count");
        action = "DISTRICT_REGISTRAR_PHYSICAL_FILE_RECEIVED";
        description = `${projectName(actionProject)} physical file received as inward ${form.inwardNumber}.`;
        patch = {
          districtPhysicalFileReceived: true,
          districtInwardNumber: form.inwardNumber.trim(),
          districtPhysicalFileNumber: form.physicalFileNumber.trim(),
          districtPhysicalFileReceivedDate: form.receivedDate,
          districtPhysicalFilePageCount: Number(form.pageCount),
          districtRegistrarStatus: "DISTRICT_PHYSICAL_FILE_RECEIVED",
          registrarWorkflowStage: "DISTRICT_PHYSICAL_FILE_RECEIVED",
          "registrarWorkflow.district.status":
            "DISTRICT_PHYSICAL_FILE_RECEIVED",
          "registrarWorkflow.district.receivedAtIso": nowIso,
        };
      }
      if (actionType === "verify") {
        if (!isPmcForwardedToDistrict(actionProject))
          throw new Error("PMC must forward the Registrar package before scrutiny.");
        const readiness = documentReadiness(actionProject);
        if (readiness.complete !== readiness.total)
          throw new Error("All mandatory project documents must be available before scrutiny.");
        requireValue(form.remarks, "Official remarks");
        const adverse = form.suoMotoStatus === "ADVERSE";
        action = adverse
          ? "DISTRICT_REGISTRAR_SUO_MOTO_REJECTED"
          : "DISTRICT_REGISTRAR_FILE_VERIFIED";
        description = adverse
          ? `${projectName(actionProject)} stopped due to adverse / suo-moto status.`
          : `${projectName(actionProject)} District file scrutiny completed and verified.`;
        patch = {
          suoMotoStatus: form.suoMotoStatus,
          districtRegistrarVerified: !adverse,
          districtRegistrarStatus: adverse
            ? "DISTRICT_SUO_MOTO_REJECTED"
            : "DISTRICT_DOCUMENTS_VERIFIED",
          districtRegistrarRemarks: form.remarks.trim(),
          registrarWorkflowStage: adverse
            ? "DISTRICT_SUO_MOTO_REJECTED"
            : "DISTRICT_DOCUMENTS_VERIFIED",
          nextResponsibleRole: adverse ? "PMC" : "DISTRICT_REGISTRAR",
          "registrarWorkflow.district.status": adverse
            ? "DISTRICT_SUO_MOTO_REJECTED"
            : "DISTRICT_DOCUMENTS_VERIFIED",
          "registrarWorkflow.district.verifiedAtIso": nowIso,
        };
      }
      if (actionType === "query") {
        requireValue(form.remarks, "Official query");
        action = "DISTRICT_REGISTRAR_FILE_QUERY_RAISED";
        description = `District Registrar query for ${projectName(actionProject)}: ${form.remarks.trim()}`;
        patch = {
          districtRegistrarStatus: "DISTRICT_QUERY_RETURNED_TO_PMC",
          districtRegistrarRemarks: form.remarks.trim(),
          districtQueryRaisedAtIso: nowIso,
          registrarWorkflowStage: "DISTRICT_QUERY_RETURNED_TO_PMC",
          nextResponsibleRole: "PMC",
          "registrarWorkflow.district.status": "DISTRICT_QUERY_RETURNED_TO_PMC",
        };
        targetRoles = ["PMC", "PACS"];
      }
      if (actionType === "stateQueryToPmc") {
        requireValue(form.remarks, "District forwarding remarks");
        if (!stateStatus(actionProject).includes("RETURNED_TO_DISTRICT"))
          throw new Error("No State Registrar query is waiting for District forwarding.");
        action = "DISTRICT_REGISTRAR_STATE_QUERY_FORWARDED_TO_PMC";
        description = `State Registrar query forwarded by District to PMC for ${projectName(actionProject)}. ${form.remarks.trim()}`;
        patch = {
          districtRegistrarStatus: "STATE_QUERY_FORWARDED_TO_PMC",
          pmcRegistrarStatus: "STATE_QUERY_PENDING_PMC_COMPLIANCE",
          districtStateQueryForwardingRemarks: form.remarks.trim(),
          registrarWorkflowStage: "STATE_QUERY_WITH_PMC_FOR_COMPLIANCE",
          nextResponsibleRole: "PMC",
          "registrarWorkflow.district.status": "STATE_QUERY_FORWARDED_TO_PMC",
          "registrarWorkflow.pmc.status": "STATE_QUERY_PENDING_PMC_COMPLIANCE",
        };
        targetRoles = ["PMC", "PACS"];
      }
      if (actionType === "stateComplianceForward") {
        requireValue(form.remarks, "District verification remarks");
        if (!districtStatus(actionProject).includes("PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED"))
          throw new Error("PMC State-query compliance has not been resubmitted yet.");
        action = "DISTRICT_REGISTRAR_STATE_COMPLIANCE_FORWARDED_TO_STATE";
        description = `District verified PMC compliance and re-forwarded ${projectName(actionProject)} to State Registrar. ${form.remarks.trim()}`;
        patch = {
          districtRegistrarStatus: "DISTRICT_COMPLIANCE_FORWARDED_TO_STATE",
          stateRegistrarStatus: "PENDING_STATE_REVIEW_AFTER_COMPLIANCE",
          districtStateComplianceRemarks: form.remarks.trim(),
          registrarWorkflowStage: "DISTRICT_COMPLIANCE_FORWARDED_TO_STATE",
          nextResponsibleRole: "STATE_REGISTRAR",
          "registrarWorkflow.district.status": "DISTRICT_COMPLIANCE_FORWARDED_TO_STATE",
          "registrarWorkflow.state.status": "PENDING_STATE_REVIEW_AFTER_COMPLIANCE",
        };
        targetRoles = ["STATE_REGISTRAR", "PACS", "PMC"];
      }
      if (actionType === "forward") {
        requireValue(form.dispatchNumber, "District dispatch number");
        requireValue(form.dispatchDate, "Dispatch date");
        requireValue(form.remarks, "Forwarding remarks");
        if (
          !(
            actionProject.districtRegistrarVerified === true ||
            districtStatus(actionProject).includes("VERIFIED")
          )
        )
          throw new Error(
            "Complete District scrutiny before State forwarding.",
          );
        if (String(actionProject.suoMotoStatus || "").toUpperCase() !== "CLEAR")
          throw new Error(
            "Suo-moto status must be CLEAR before State forwarding.",
          );
        const forwardingLetter = file
          ? await uploadOfficialFile({ file, role: "DISTRICT_REGISTRAR", uid: profile.id, projectId: actionProject.id, folder: "state_forwarding_letter" })
          : null;
        action = "DISTRICT_REGISTRAR_FILE_FORWARDED_TO_STATE";
        notificationType = "DISTRICT_FILE_FORWARDED_TO_STATE";
        description = `${projectName(actionProject)} forwarded to State Registrar under dispatch ${form.dispatchNumber}.`;
        targetRoles = ["STATE_REGISTRAR", "PACS", "PMC"];
        patch = {
          districtPhysicalFileForwardedToState: true,
          districtToStateDispatchNumber: form.dispatchNumber.trim(),
          districtToStateDispatchDate: form.dispatchDate,
          districtForwardingLetterUrl: forwardingLetter?.url || actionProject.districtForwardingLetterUrl || "",
          districtForwardingLetterPath: forwardingLetter?.path || actionProject.districtForwardingLetterPath || "",
          districtForwardingLetterName: forwardingLetter?.name || actionProject.districtForwardingLetterName || "",
          districtRegistrarStatus: "DISTRICT_FILE_FORWARDED_TO_STATE",
          districtRegistrarRemarks: form.remarks.trim(),
          stateRegistrarStatus: "WAITING_FOR_STATE_INWARD",
          registrarWorkflowStage: "PENDING_STATE_REGISTRAR_INWARD",
          nextResponsibleRole: "STATE_REGISTRAR",
          "registrarWorkflow.district.status":
            "DISTRICT_FILE_FORWARDED_TO_STATE",
          "registrarWorkflow.district.forwardedAtIso": nowIso,
          ...(forwardingLetter ? { "registrarWorkflow.district.forwardingLetter": forwardingLetter } : {}),
        };
      }
      if (actionType === "dcdcSchedule") {
        requireValue(form.meetingNumber, "DCDC meeting number");
        requireValue(form.meetingDate, "Meeting date");
        requireValue(form.meetingTime, "Meeting time");
        requireValue(form.venue, "Meeting venue");
        requireValue(form.remarks, "Meeting remarks");
        if (!isStateApproved(actionProject))
          throw new Error(
            "State project permission is mandatory before DCDC scheduling.",
          );
        action = "DISTRICT_REGISTRAR_DCDC_MEETING_SCHEDULED";
        description = `${projectName(actionProject)} DCDC meeting scheduled for ${form.meetingDate} at ${form.meetingTime}.`;
        targetRoles = ["PACS", "PMC", "STATE_REGISTRAR"];
        patch = {
          dcdcStatus: "SCHEDULED",
          dcdcMeetingNumber: form.meetingNumber.trim(),
          dcdcMeetingDate: form.meetingDate,
          dcdcMeetingTime: form.meetingTime,
          dcdcMeetingVenue: form.venue.trim(),
          dcdcRemarks: form.remarks.trim(),
          registrarWorkflowStage: "DCDC_MEETING_SCHEDULED",
          nextResponsibleRole: "DISTRICT_REGISTRAR_DCDC",
        };
      }
      if (actionType === "dcdcDecision") {
        requireValue(form.remarks, "Decision remarks");
        if (!actionProject.dcdcMeetingDate)
          throw new Error(
            "Schedule the DCDC meeting before recording a decision.",
          );
        let letter = {};
        if (form.decision !== "QUERY")
          letter = await uploadOfficialFile({
            file,
            role: "DISTRICT_REGISTRAR",
            uid: profile.id,
            projectId: actionProject.id,
            folder: "dcdc_decision",
          });
        action = `DISTRICT_REGISTRAR_DCDC_${form.decision}`;
        description = `${projectName(actionProject)} DCDC decision: ${human(form.decision)}. ${form.remarks.trim()}`;
        targetRoles = ["PACS", "PMC", "STATE_REGISTRAR"];
        patch = {
          dcdcStatus: form.decision,
          dcdcRemarks: form.remarks.trim(),
          dcdcDecisionDate: new Date().toISOString().slice(0, 10),
          dcdcDecisionLetterUrl:
            letter.url || actionProject.dcdcDecisionLetterUrl || "",
          dcdcDecisionLetterPath:
            letter.path || actionProject.dcdcDecisionLetterPath || "",
          registrarWorkflowStage:
            form.decision === "APPROVED"
              ? "DCDC_APPROVED_REGISTRAR_PROCESS_COMPLETE"
              : form.decision === "QUERY"
                ? "DCDC_QUERY_PENDING_COMPLIANCE"
                : "DCDC_REJECTED",
          nextResponsibleRole:
            form.decision === "APPROVED"
              ? "NO_FURTHER_REGISTRAR_ACTION"
              : "PACS_PMC",
          "registrarWorkflow.district.dcdcLetter": letter,
        };
      }
      if (actionType === "subsidyQuery") {
        requireValue(form.remarks, "Subsidy query");
        action = "DISTRICT_REGISTRAR_SUBSIDY_QUERY_RAISED";
        description = `District subsidy query for ${projectName(actionProject)}: ${form.remarks.trim()}`;
        patch = {
          districtSubsidyStatus: "QUERY",
          districtSubsidyRemarks: form.remarks.trim(),
          subsidyAuthorityStage: "DISTRICT_QUERY_RETURNED_TO_PMC_PACS",
          nextResponsibleRole: "PMC_PACS",
        };
      }
      if (actionType === "subsidyForward") {
        requireValue(form.dispatchNumber, "Subsidy dispatch number");
        requireValue(form.remarks, "Forwarding remarks");
        action = "DISTRICT_REGISTRAR_SUBSIDY_FILE_FORWARDED_TO_STATE";
        description = `${projectName(actionProject)} subsidy file forwarded to State under dispatch ${form.dispatchNumber}.`;
        targetRoles = ["STATE_REGISTRAR", "PACS", "PMC"];
        patch = {
          districtSubsidyStatus: "FORWARDED_TO_STATE",
          districtSubsidyDispatchNumber: form.dispatchNumber.trim(),
          districtSubsidyRemarks: form.remarks.trim(),
          subsidyAuthorityStage: "DISTRICT_VERIFIED_FORWARDED_TO_STATE",
          nextResponsibleRole: "STATE_REGISTRAR",
        };
      }
      await runRegistrarAction({
        project: actionProject,
        profile,
        action,
        message: description,
        patch,
        notificationType,
        targetRoles,
      });
      setMessage("Official action saved successfully with live audit trail.");
      setActionType("");
      setFile(null);
    } catch (actionError) {
      setMessage(actionError.message || "Unable to save the authority action.");
    } finally {
      setBusy(false);
    }
  };

  const exportDistrictMis = () => {
    const rows = [
      [
        "Application ID",
        "PACS / Society",
        "Registration No.",
        "Village",
        "Taluka",
        "District",
        "Capacity MT",
        "PMC",
        "Registrar Package",
        "District Status",
        "State Status",
        "DCDC Status",
        "Subsidy Status",
        "Updated",
      ],
    ];
    projects.forEach((project) =>
      rows.push([
        projectId(project),
        projectName(project),
        project.registrationNumber || project.pacsRegistrationNo,
        project.village || project.villageName,
        project.taluka || project.talukaName || project.block,
        project.district || project.districtName,
        capacityMt(project),
        project.assignedPmcName || project.pmcName,
        registrarPackageUrl(project) ? "READY" : "PENDING",
        districtStatus(project),
        project.stateRegistrarStatus,
        project.dcdcStatus,
        project.districtSubsidyStatus || project.subsidyWorkflowStatus,
        dateTime(project.updatedAt || project.createdAt),
      ]),
    );
    downloadCsv(
      `District_Registrar_MIS_${profile.district}_${new Date().toISOString().slice(0, 10)}.csv`,
      rows,
    );
  };

  if (sessionLoading)
    return <LoadingScreen title="Loading District Registrar Control Centre" />;
  if (sessionError || !profile)
    return (
      <LoadingScreen
        error={sessionError || "District Registrar profile is unavailable."}
      />
    );

  const renderModule = () => {
    if (active === "overview")
      return (
        <>
          <PageTitle
            eyebrow="DISTRICT GOVERNANCE · LIVE AUTHORITY CONTROL"
            title={`${profile.district || "District"} Registrar Command Centre`}
            description="See every PACS application in the District, scrutinise PMC-forwarded digital files, return precise queries to PMC, forward compliant files to State and record the DCDC result."
          />
          <section className="rg-hero">
            <div>
              <small>WORLD'S LARGEST GRAIN STORAGE PLAN</small>
              <h2>One PACS, one serial file, one permanent authority trail.</h2>
              <p>
                PACS, PMC, District and State use one live project record. No
                duplicate register and no hidden status hand-off.
              </p>
            </div>
            <aside>
              <span>
                <b>{projects.length}</b>
                <small>District projects</small>
              </span>
              <span>
                <b>{number(metrics.capacity)} MT</b>
                <small>Proposed capacity</small>
              </span>
            </aside>
          </section>
          <section className="rg-metrics">
            <Metric
              label="PMC Forwarded Files"
              value={stages.inbox.length}
              note="Ready for District scrutiny"
              tone="orange"
            />
            <Metric
              label="Under District Scrutiny"
              value={stages.scrutiny.length}
              note="Complete ZIP and documents"
              tone="blue"
            />
            <Metric
              label="Ready for State"
              value={stages.forward.length}
              note="Verified and suo-moto clear"
              tone="green"
            />
            <Metric
              label="DCDC Cases"
              value={stages.dcdc.length}
              note={`${metrics.dcdcApproved} approved`}
              tone="purple"
            />
            <Metric
              label="District PACS"
              value={metrics.pacs}
              note="Live project applicants"
              tone="green"
            />
            <Metric
              label="Allocated PMC"
              value={metrics.pmc}
              note="Working in jurisdiction"
              tone="blue"
            />
            <Metric
              label="Forwarded to State"
              value={metrics.forwarded}
              note="Permanent dispatch record"
              tone="purple"
            />
            <Metric
              label="Queries & Compliance"
              value={stages.queries.length}
              note="PMC response chain"
              tone="orange"
            />
          </section>
          <AutomationQueue
            projects={projects}
            scope="DISTRICT"
            onOpen={(project, key) => {
              setActive(key);
              setSelectedId(project.id);
            }}
          />
          <div className="rg-grid-2">
            <section className="rg-panel">
              <div className="rg-panel-head">
                <div>
                  <small>PRIORITY QUEUE</small>
                  <h3>Files Requiring District Action</h3>
                  <p>
                    Each queue opens a different filtered authority workspace.
                  </p>
                </div>
              </div>
              <div className="rg-table-wrap">
                <table className="rg-table" style={{ minWidth: 0 }}>
                  <tbody>
                    {[
                      [
                        "PMC-forwarded files awaiting scrutiny",
                        stages.inbox.length,
                        "inbox",
                      ],
                      [
                        "Digital files under scrutiny",
                        stages.scrutiny.length,
                        "scrutiny",
                      ],
                      [
                        "District / State queries in compliance",
                        stages.queries.length,
                        "queries",
                      ],
                      [
                        "Verified files ready for State",
                        stages.forward.length,
                        "forward",
                      ],
                      ["State-approved DCDC cases", stages.dcdc.length, "dcdc"],
                    ].map(([label, count, key]) => (
                      <tr key={key}>
                        <td>
                          <b>{label}</b>
                        </td>
                        <td>
                          <StatusPill value={`${count} files`} />
                        </td>
                        <td>
                          <button
                            className="rg-link"
                            onClick={() => setActive(key)}
                          >
                            Open Workspace →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="rg-panel">
              <div className="rg-panel-head">
                <div>
                  <small>LIVE DISTRICT ACTIVITY</small>
                  <h3>Latest Updates</h3>
                </div>
                <button
                  className="rg-link"
                  onClick={() => setActive("activity")}
                >
                  View all
                </button>
              </div>
              <ActivityList items={news} limit={7} />
            </section>
          </div>
        </>
      );
    const moduleMap = {
      all: [
        "All PACS Applications",
        "Complete live list of every active PACS application in this District, including PMC, documents and current authority stage.",
        stages.all,
        "No PACS application is available in this District.",
      ],
      inbox: [
        "PMC Forwarded File Inbox",
        "Complete Registrar ZIPs and linked documents formally forwarded by the allocated PMC.",
        stages.inbox,
        "No complete PACS Registrar file is waiting for receipt.",
      ],
      scrutiny: [
        "District Document Scrutiny",
        "PMC-forwarded files under serial-wise document and adverse-status verification.",
        stages.scrutiny,
        "No physical file is pending District scrutiny.",
      ],
      queries: [
        "Query & Compliance Desk",
        "District queries sent to PMC and State queries routed State → District → PMC with live compliance responses.",
        stages.queries,
        "No District Registrar query is pending.",
      ],
      forward: [
        "State Forwarding Desk",
        "Only District-verified and suo-moto-clear cases eligible for State dispatch.",
        stages.forward,
        "No verified file is ready for State forwarding.",
      ],
      dcdc: [
        "DCDC Decision Desk",
        "Only State-permitted cases available for DCDC scheduling and decision.",
        stages.dcdc,
        "No State-permitted DCDC case is available.",
      ],
      subsidy: [
        "Work-Start Subsidy File Desk",
        "Survey, soil test, design, estimate and DPR packages submitted after work starts.",
        stages.subsidy,
        "No work-start subsidy package is available.",
      ],
    };
    if (moduleMap[active]) {
      const [title, description, rows, emptyText] = moduleMap[active];
      return (
        <>
          <PageTitle
            eyebrow="DISTRICT REGISTRAR OFFICIAL WORKSPACE"
            title={title}
            description={description}
          />
          <ProjectRegister
            title={title}
            description={description}
            rows={rows}
            search={search}
            setSearch={setSearch}
            onOpen={(project) => setSelectedId(project.id)}
            emptyText={emptyText}
          />
        </>
      );
    }
    if (active === "activity")
      return (
        <>
          <PageTitle
            eyebrow="PERMANENT LIVE AUDIT"
            title="District Activity & Notification Trail"
            description="PACS, PMC, District and State actions within this jurisdiction, newest first."
          />
          <section className="rg-panel">
            <ActivityList items={news} />
          </section>
        </>
      );
    return (
      <>
        <PageTitle
          eyebrow="DISTRICT MANAGEMENT INFORMATION SYSTEM"
          title="District Registrar MIS Reports"
          description="Download a clean PACS-wise register containing stage, capacity, PMC, DCDC and subsidy status."
          actions={
            <button className="rg-btn green" onClick={exportDistrictMis}>
              Download District MIS CSV
            </button>
          }
        />
        <section className="rg-metrics">
          <Metric
            label="Total Projects"
            value={projects.length}
            note="Live NAFED applications"
          />
          <Metric
            label="Total Capacity"
            value={`${number(metrics.capacity)} MT`}
            note="Proposed storage"
            tone="green"
          />
          <Metric
            label="District Queries"
            value={stages.queries.length}
            note="Compliance pending"
            tone="orange"
          />
          <Metric
            label="DCDC Approved"
            value={metrics.dcdcApproved}
            note="Registrar decision complete"
            tone="purple"
          />
        </section>
        <ProjectRegister
          title="Complete District Project Register"
          description="Every active project in the approved District jurisdiction."
          rows={projects}
          search={search}
          setSearch={setSearch}
          onOpen={(project) => setSelectedId(project.id)}
          emptyText="No NAFED project is available in this district."
        />
      </>
    );
  };

  return (
    <PortalShell
      authority="District Registrar Portal"
      subtitle="World's Largest Grain Storage Plan · District Scrutiny & DCDC"
      profile={profile}
      navigation={navigation}
      active={active}
      onNavigate={(key) => {
        setActive(key);
        setSelectedId("");
        setMessage("");
      }}
      news={news}
    >
      {(error || message) && (
        <div
          className={`rg-alert ${error || /unable|required|error/i.test(message) ? "error" : "success"}`}
        >
          {error || message}
        </div>
      )}
      {loading ? (
        <LoadingScreen title="Loading live District records" />
      ) : (
        renderModule()
      )}
      <CaseDrawer
        project={selectedProject}
        activeModule={active}
        onClose={() => setSelectedId("")}
        onAction={openAction}
      />
      <ActionModal
        type={actionType}
        form={form}
        setForm={setForm}
        setFile={setFile}
        onClose={() => setActionType("")}
        onSubmit={saveAction}
        busy={busy}
      />
    </PortalShell>
  );
}
